import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Bolt as BoltIcon,
  ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import { myStyles as styles, fetchData } from "../libs/util";
import { CustomSnackbar } from "./SnackbarComponent";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { firebaseHelper, useCreditStore, BuyCreditsModal } from "ui";

interface TeamCreditsProps {
  designFirmID: string | null;
}

export const TeamCredits = ({ designFirmID }: TeamCreditsProps) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showBuyModal, setShowBuyModal] = useState(false);

  // Use store state for balance, loading, and error
  const { teamBalance, isLoading, error, fetchTeamBalance } = useCreditStore();

  // Fetch user permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        const permissionsRef = doc(firebaseHelper.db, "permissions", user.uid);
        const permissionsDoc = await getDoc(permissionsRef);

        if (permissionsDoc.exists()) {
          const permissions = permissionsDoc.data();
          setIsAdmin((permissions.teamsAdmin || []).includes(designFirmID));
        }
      } catch (err) {
        console.error("[TeamCredits] Error checking permissions:", err);
      }
    };

    checkPermissions();
  }, [designFirmID]);

  // Fetch team balance using the store
  const loadTeamBalance = useCallback(async () => {
    if (!designFirmID) return;

    try {
      await fetchTeamBalance(designFirmID);
    } catch (err) {
      console.error("[TeamCredits] Error fetching team balance:", err);
    }
  }, [designFirmID, fetchTeamBalance]);

  useEffect(() => {
    loadTeamBalance();
  }, [loadTeamBalance]);

  const handlePurchaseCredits = () => {
    setShowBuyModal(true);
  };

  const handleModalClose = () => {
    setShowBuyModal(false);
    if (designFirmID) {
      fetchTeamBalance(designFirmID, true);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.container}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: "bold", mb: 3 }}
        >
          Team Credits
        </Typography>
        <Card sx={styles.card}>
          <CardContent sx={styles.emptyState}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
            <Button
              onClick={loadTeamBalance}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const credits = teamBalance?.totalBalance ?? 0;
  const isLowBalance = credits < 10;

  return (
    <>
      <Box sx={styles.container}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          Team Credits
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          Manage your team's credit balance. Credits are used for concept
          generation.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {/* Current Balance */}
          <Grid item xs={12} md={6}>
            <Card sx={styles.card}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  Current Balance
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <BoltIcon
                    sx={{
                      fontSize: "3rem",
                      color: isLowBalance ? "#EF4444" : "#F59E0B",
                    }}
                  />
                  <Typography
                    variant="h2"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {credits}
                  </Typography>
                </Box>

                <Chip
                  label={isLowBalance ? "Low Balance" : "Healthy"}
                  size="small"
                  sx={
                    isLowBalance
                      ? {
                          bgcolor: "#FEE2E2",
                          color: "#991B1B",
                          fontWeight: "bold",
                        }
                      : {
                          bgcolor: "#D1FAE5",
                          color: "#065F46",
                          fontWeight: "bold",
                        }
                  }
                />

                {teamBalance?.lastUpdated && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mt: 2 }}
                  >
                    Last updated:{" "}
                    {new Date(teamBalance.lastUpdated).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={6}>
            <Card sx={styles.card}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  Actions
                </Typography>

                <Stack spacing={2}>
                  {isAdmin && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ShoppingCartIcon />}
                      onClick={handlePurchaseCredits}
                      fullWidth
                    >
                      Purchase Credits
                    </Button>
                  )}

                  {!isAdmin && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", textAlign: "center" }}
                    >
                      Contact your team admin to purchase more credits.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Credit Usage Info */}
        <Card sx={styles.card}>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              Credit Usage
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              • Each concept generation costs <strong>1 credit</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              • Credits are shared across all team members
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        open={showBuyModal}
        onClose={handleModalClose}
        uid={getAuth().currentUser?.uid}
        fetchData={fetchData}
      />
    </>
  );
};
